// src/pages/finance/settings/InvoicingSettingsTab.tsx
//
// The clinic's own identity and invoicing defaults — the singleton `clinic_settings` row.
//
// ⚠️ The endpoint is `GET`/`PUT /clinic-settings`, NOT `/finance/clinic-settings`. The `finance/`
// segment in the platform URL belongs to the gateway and is already inside `VITE_FINANCE_SERVER`.
//
// ⚠️ Editing anything here changes FUTURE invoices only. The clinic identity block (legal name,
// ABN, address, contact) is SNAPSHOTTED onto each invoice when it is issued, precisely so that an
// issued tax invoice renders identically forever. Bank details and the footer are read live at
// render time and are not snapshotted.
import React, { useEffect } from "react";
import { Alert, Button, Col, Form, Input, InputNumber, Row, Select, Switch, Typography } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SectionCard } from "../../../components/shared";

import { fetchClinicSettings, updateClinicSettings } from "../../../api/finance/FinanceApi";
import { financeKeys } from "../../../api/finance/financeQueryKeys";
import {
	ClinicSettings,
	PayIdType,
	UpdateClinicSettingsRequest,
} from "../../../types/finance/FinanceTypes";
import { PriceEntryMode, TAX_CODE_LABELS, TaxCode } from "../../../utils/finance/gst";
import { financeErrorMessage } from "../../../utils/finance/financeError";
import { useNotifications } from "../../../context/NotificationContext";
import { buildClinicSettingsBody, stripSeparators } from "./clinicSettingsRequest";

const { Text } = Typography;

const TAX_CODE_OPTIONS = (Object.keys(TAX_CODE_LABELS) as TaxCode[]).map((code) => ({
	value: code,
	label: TAX_CODE_LABELS[code],
}));

const InvoicingSettingsTab: React.FC = () => {
	const [form] = Form.useForm<UpdateClinicSettingsRequest>();
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotifications();

	const { data: settings, isLoading } = useQuery({
		queryKey: financeKeys.clinicSettings(),
		queryFn: fetchClinicSettings,
	});

	useEffect(() => {
		if (settings) {
			form.setFieldsValue(settings as unknown as UpdateClinicSettingsRequest);
		}
	}, [settings, form]);

	const mutation = useMutation({
		// The body is built by a pure function so the "emptying a field clears it" rule is
		// testable without rendering — see `clinicSettingsRequest.ts`.
		mutationFn: (values: UpdateClinicSettingsRequest) =>
			updateClinicSettings(buildClinicSettingsBody(values as unknown as Record<string, unknown>)),
		onSuccess: (saved: ClinicSettings) => {
			queryClient.setQueryData(financeKeys.clinicSettings(), saved);
			// Invoices render clinic details, so a settings change must not leave a stale document
			// on screen behind it.
			queryClient.invalidateQueries({ queryKey: financeKeys.invoices() });
			showSuccess("Clinic settings saved");
		},
		onError: (error) => showError(financeErrorMessage(error, "Failed to save clinic settings")),
	});

	const gstRegistered = Form.useWatch("gstRegistered", form);
	const abn = Form.useWatch("abn", form);
	const willIssueTaxInvoices = !!gstRegistered && stripSeparators(abn).length === 11;

	return (
		<Form
			form={form}
			layout="vertical"
			disabled={isLoading}
			initialValues={{
				gstRegistered: false,
				invoiceNumberPrefix: "INV-",
				invoiceNumberPadding: 5,
				defaultPaymentTermsDays: 14,
				defaultTaxCode: TaxCode.GST_FREE_HEALTH,
				defaultPriceEntryMode: PriceEntryMode.INCLUSIVE,
				payLinkTtlDays: 30,
			}}
			onFinish={(values) => mutation.mutate(values)}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }} data-testid="finance-settings-root">
				<SectionCard
					title="Clinic identity"
					subtitle="Snapshotted onto every invoice at the moment it is issued — changing it never alters an invoice already issued."
					extra={
						<Button
							type="primary"
							icon={<SaveOutlined />}
							loading={mutation.isPending}
							onClick={() => form.submit()}
							data-testid="finance-settings-save"
						>
							Save
						</Button>
					}
				>
					{/*
					  The whole "Tax Invoice" wording rule, stated where it is decided. The
					  server makes this call at issue and freezes the answer onto the row;
					  showing it here stops a clinic wondering why its documents say
					  "Invoice".
					*/}
					<Alert
						type={willIssueTaxInvoices ? "success" : "info"}
						showIcon
						style={{ marginBottom: 16 }}
						message={
							willIssueTaxInvoices
								? "Invoices issued from now on will be headed “Tax Invoice”."
								: "Invoices will be headed “Invoice”, with no GST summary."
						}
						description="A document may only be called a tax invoice when the clinic has an ABN and is registered for GST."
					/>

					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item
								label="Legal name"
								name="legalName"
								rules={[{ required: true, message: "The registered entity name is required" }]}
							>
								<Input maxLength={255} placeholder="Registered entity name" />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Trading name" name="tradingName">
								<Input maxLength={255} placeholder="If it differs from the legal name" />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								label="ABN"
								name="abn"
								normalize={stripSeparators}
								rules={[
									{
										validator: (_, value) =>
											!value || /^\d{11}$/.test(String(value))
												? Promise.resolve()
												: Promise.reject(new Error("abn must be 11 digits")),
									},
								]}
								extra="11 digits. Spaces are stripped."
							>
								<Input maxLength={11} placeholder="12345678901" />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Registered for GST" name="gstRegistered" valuePropName="checked">
								<Switch data-testid="finance-settings-gst" />
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={[16, 0]}>
						<Col xs={24} md={12}>
							<Form.Item label="Street" name="street">
								<Input maxLength={255} />
							</Form.Item>
						</Col>
						<Col xs={24} md={4}>
							<Form.Item label="Suburb" name="suburb">
								<Input maxLength={255} />
							</Form.Item>
						</Col>
						<Col xs={12} md={4}>
							<Form.Item label="State" name="state">
								<Input maxLength={32} placeholder="VIC" />
							</Form.Item>
						</Col>
						<Col xs={12} md={4}>
							<Form.Item label="Postcode" name="postcode">
								<Input maxLength={16} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item
								label="Contact email"
								name="contactEmail"
								rules={[{ type: "email", message: "Enter a valid email address" }]}
							>
								<Input maxLength={255} />
							</Form.Item>
						</Col>
						<Col xs={24} md={12}>
							<Form.Item label="Contact phone" name="contactPhone">
								<Input maxLength={64} />
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard
					title="Invoice numbering"
					subtitle="The counter is continuous and never restarts — nothing here encodes a date."
				>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Prefix" name="invoiceNumberPrefix">
								<Input maxLength={16} placeholder="INV-" />
							</Form.Item>
						</Col>
						<Col xs={24} md={8}>
							<Form.Item
								label="Minimum digits"
								name="invoiceNumberPadding"
								extra="5 gives INV-00042. A counter past the padding simply gets longer."
							>
								<InputNumber min={1} max={12} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard
					title="Invoicing defaults"
					subtitle="Applied to a new invoice when the operator does not override them."
				>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={6}>
							<Form.Item label="Payment terms (days)" name="defaultPaymentTermsDays">
								<InputNumber min={0} max={365} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="Default tax treatment" name="defaultTaxCode">
								<Select options={TAX_CODE_OPTIONS} />
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item label="Price entry" name="defaultPriceEntryMode">
								<Select
									options={[
										{ value: PriceEntryMode.INCLUSIVE, label: "Prices include GST" },
										{ value: PriceEntryMode.EXCLUSIVE, label: "Prices exclude GST" },
									]}
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={6}>
							<Form.Item
								label="Pay link validity (days)"
								name="payLinkTtlDays"
								extra="How long a generated payment link stays usable."
							>
								<InputNumber min={1} max={365} style={{ width: "100%" }} />
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard
					title="Bank details"
					subtitle="Shown on invoices so a patient can pay by transfer."
				>
					<Alert
						type="warning"
						showIcon
						style={{ marginBottom: 16 }}
						message="These are printed on every invoice the clinic sends."
						description="They are read live rather than snapshotted, so changing them changes what already-issued invoices display."
					/>
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item label="Account name" name="bankAccountName">
								<Input maxLength={255} />
							</Form.Item>
						</Col>
						<Col xs={12} md={8}>
							<Form.Item
								label="BSB"
								name="bankBsb"
								normalize={stripSeparators}
								rules={[
									{
										validator: (_, value) =>
											!value || /^\d{6}$/.test(String(value))
												? Promise.resolve()
												: Promise.reject(new Error("bankBsb must be 6 digits")),
									},
								]}
								extra="6 digits. 123-456 is accepted."
							>
								<Input maxLength={7} placeholder="123456" />
							</Form.Item>
						</Col>
						<Col xs={12} md={8}>
							<Form.Item label="Account number" name="bankAccountNumber">
								<Input maxLength={32} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[16, 0]}>
						<Col xs={24}>
							<Form.Item
								label="Invoice payment link"
								name="invoicePaymentLink"
								extra="Optional. An alternative to bank details — a non-Stripe invoice can be set to show this link instead. The choice is made per invoice when it is created."
							>
								<Input
									maxLength={2048}
									placeholder="https://pay.example.com/your-clinic"
									data-testid="finance-settings-payment-link"
								/>
							</Form.Item>
						</Col>
					</Row>
					{/*
					  PAYID: a clinic-wide PayID (a mobile number or an email) — a third
					  alternative offered per invoice alongside the bank details and the link.
					  Optional, read live like the bank details.
					*/}
					<Row gutter={[16, 0]}>
						<Col xs={24} md={8}>
							<Form.Item
								label="PayID type"
								name="payIdType"
								extra="Optional. Choose whether the clinic's PayID is a mobile number or an email."
							>
								<Select
									allowClear
									placeholder="Mobile or email"
									options={[
										{ value: PayIdType.MOBILE, label: "Mobile" },
										{ value: PayIdType.EMAIL, label: "Email" },
									]}
									data-testid="finance-settings-payid-type"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} md={16}>
							<Form.Item
								label="PayID"
								name="payId"
								extra="Optional. The mobile number or email a patient can pay to via PayID."
							>
								<Input
									maxLength={256}
									placeholder="0400 000 000 or pay@your-clinic.com"
									data-testid="finance-settings-payid"
								/>
							</Form.Item>
						</Col>
					</Row>
				</SectionCard>

				<SectionCard title="Invoice footer">
					<Form.Item name="invoiceFooterText">
						<Input.TextArea
							rows={3}
							maxLength={2000}
							placeholder="Terms, remittance instructions, thank-you note…"
						/>
					</Form.Item>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Printed at the bottom of every invoice.
					</Text>
				</SectionCard>

				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button
						type="primary"
						icon={<SaveOutlined />}
						loading={mutation.isPending}
						onClick={() => form.submit()}
					>
						Save settings
					</Button>
				</div>
			</div>
		</Form>
	);
};

export default InvoicingSettingsTab;
